import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { fromJS, List } from 'immutable';
import { Subject } from 'rxjs';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import {
  Discount,
  DiscountTypesEnum,
} from '../../../document-data/sub-models/discount.model';
import { IValueSwitch } from '../../../form-master/components/value-switch-field/value-switch-field.component';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { DocumentDiscountBuilder } from './document-discount-dialog.builder';
import { filter, takeUntil } from 'rxjs/operators';

export const DIALOG_TYPE = 'document-discount';

export interface IDocumentDiscountDialogData {
  discount: Discount;
  currencyCode: string;
  locale: string;
  trackingDialogName: TrackingDialogNamesEnum;
  trackingScreenName: TrackingScreenNamesEnum;
}

@Component({
  selector: 'app-document-discount-dialog',
  templateUrl: './document-discount-dialog.component.html',
  styleUrls: ['./document-discount-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DocumentDiscountBuilder],
})
export class DocumentDiscountDialogComponent implements OnInit, OnDestroy {
  @Output() onUpdateDiscount: EventEmitter<any> = new EventEmitter<any>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  discount: Discount;
  form: FormGroup;
  formKeys: any;
  discountSwitches: List<IValueSwitch>;
  DiscountTypesEnum: typeof DiscountTypesEnum;
  dialogType: string;
  currencyCode: string;
  locale: string;

  trackingObject: ITrackingObject;
  trackingDialogName: TrackingDialogNamesEnum;
  trackingScreenName: TrackingScreenNamesEnum;
  saveAction: ITrackingAction;
  cancelAction: ITrackingAction;

  constructor(
    private _dialogService: DialogService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _documentDiscountBuilder: DocumentDiscountBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  ngOnInit() {
    this.formKeys = this._documentDiscountBuilder.FORM_KEYS;
    this.dialogType = DIALOG_TYPE;
    this.DiscountTypesEnum = DiscountTypesEnum;

    this._dialogService.dialogTriggers$
      .pipe(
        filter((dialogTrigger: IDialogTrigger) => {
          return (
            dialogTrigger.type === this.dialogType &&
            dialogTrigger.action === DialogActionsEnum.open
          );
        }),
        takeUntil(this._destroyed)
      )
      .subscribe((dialogTrigger: IDialogTrigger) => {
        const dialogData: IDocumentDiscountDialogData = dialogTrigger.data;
        const discount: Discount = dialogData.discount;

        this.discount = discount;
        this.currencyCode = dialogData.currencyCode;
        this.locale = dialogData.locale;
        this.form = this._documentDiscountBuilder.buildForm(discount);
        this.trackingObject = this._trackingObjectBuilder.buildDialog(
          dialogData.trackingDialogName
        );
        this.trackingDialogName = dialogData.trackingDialogName;
        this.trackingScreenName = dialogData.trackingScreenName;

        this.saveAction = this._trackingActionBuilder.buildButtonTapped(
          'save',
          this.trackingScreenName
        );
        this.cancelAction = this._trackingActionBuilder.buildButtonTapped(
          'cancel',
          this.trackingScreenName
        );
        this.discountSwitches = this._documentDiscountBuilder.buildDiscountSwitches(
          this.currencyCode,
          this.trackingObject,
          this.trackingScreenName
        );

        this._changeDetectorRef.markForCheck();
        this._changeDetectorRef.detectChanges();
      });
  }

  onSave() {
    this.onUpdateDiscount.emit(
      this._documentDiscountBuilder.buildDiscountProps(fromJS(this.form.value))
    );
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
